import React, { useRef } from "react";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { useQuiz } from "./hooks/useQuiz";
import { STEPS, TOTAL_STEPS, stepToNumber, stepToTitle } from "./constants";

import QuizContainer from "./components/layout/QuizContainer";
import FirstStepContainer from "./components/layout/FirstStepContainer";
import Step1Jurisdiction from "./components/steps/Step1_Jurisdiction/Step1_Jurisdiction";
import Step2Details from "./components/steps/Step2_Details/Step2_Details";
import LoadingScreen from "./components/steps/LoadingScreen";
import Step3Strategy from "./components/steps/Step3Strategy";
import Step4Packages from "./components/steps/Step4Packages";
import Step4LegalCheck from "./components/steps/Step4_LegalCheck/Step4_LegalCheck";
import Step5Calculation from "./components/steps/Step5_Calculation";
import Step6Checkout from "./components/steps/Step6_Checkout";
import ThankYouPage from "./components/steps/ThankYouPage";
import StepConsultation from "./components/steps/Step_Consultation";
import TopBar from "./components/ui/TopBar/TopBar";
import InfoBox from "./components/ui/InfoBox/InfoBox";
import "./transitions.scss";

const Quiz = ({ onShowComparisonPopup }) => {
  const {
    currentStep,
    quizData,
    direction,
    calculationResult,
    analysisResult,
    isLoading,
    error,
    handleNextStep,
    handleBack,
  } = useQuiz();

  const nodeRef = useRef(null);

  const renderStep = () => {
    // Use isLoading for the loading screen, not a separate step
    if (isLoading && currentStep !== STEPS.CALCULATION) { // Step 5 has its own internal loading state for calculation
      return <LoadingScreen />;
    }

    // Wrap steps that use QuizContainer to include the error inside the container
    const withContainer = (Component, props = {}) => (
      <QuizContainer {...commonProps}>
        {error && <div style={{ marginBottom: '24px' }}><InfoBox message={error} type="error" large /></div>}
        {!error || currentStep !== STEPS.STRATEGY ? <Component onNextStep={handleNextStep} {...props} /> : null}
      </QuizContainer>
    );

    switch (currentStep) {
      case STEPS.JURISDICTION:
        return (
          <FirstStepContainer>
            {error && <div style={{ marginBottom: '24px' }}><InfoBox message={error} type="error" large /></div>}
            <Step1Jurisdiction onNextStep={handleNextStep} />
          </FirstStepContainer>
        );
      case STEPS.CONSULTATION_INTL:
        return withContainer(StepConsultation, { quizData });
      case STEPS.DETAILS_UA:
      case STEPS.DETAILS_EU:
        return withContainer(Step2Details, {
          title: `Details for ${currentStep === STEPS.DETAILS_UA ? 'Ukraine' : 'EU'}`,
          quizData,
        });
      case STEPS.STRATEGY:
        return withContainer(Step3Strategy, { analysisResult });
      case STEPS.PACKAGES:
        return withContainer(Step4Packages, { onShowComparison: onShowComparisonPopup });
      case STEPS.LEGAL_CHECK:
        return withContainer(Step4LegalCheck, {
          initialNeedsSearch: quizData.needsSearch !== undefined ? quizData.needsSearch : true,
        });
      case STEPS.CALCULATION:
        return withContainer(Step5Calculation, { calculationResult, isLoading, quizData });
      case STEPS.CHECKOUT:
        return withContainer(Step6Checkout, { quizData, calculationResult });
      case "thankyou":
        return <ThankYouPage />;
      default:
        return <div>Not Found</div>;
    }
  };

  const commonProps = {
    stepId: currentStep,
    title: stepToTitle[currentStep],
    currentStep: stepToNumber[currentStep],
    totalSteps: TOTAL_STEPS,
    onBack: handleBack,
  };

  const stepKey = (isLoading && currentStep !== STEPS.CALCULATION) ? 'loading' : currentStep;

  return (
    <div className="transition-wrapper">
      {currentStep !== STEPS.JURISDICTION && stepKey !== 'loading' && currentStep !== 'thankyou' && (
        <TopBar
          {...commonProps}
          totalSteps={currentStep === STEPS.CONSULTATION_INTL ? 2 : TOTAL_STEPS}
        />
      )}
      <TransitionGroup className={`transition-group`}>
        <CSSTransition key={stepKey} timeout={500} classNames={`slide-${direction}`} nodeRef={nodeRef}>
          <div ref={nodeRef}>{renderStep()}</div>
        </CSSTransition>
      </TransitionGroup>
    </div>
  );
};

export default Quiz;
